import {
  InquisitiveCurriculum,
  InquisitiveYears,
  InquisitiveSubject,
} from 'global/graphql/scalars.type';
import {
  EVENT_CATEGORY_PLANNER,
  EVENT_CATEGORY_CONTENT_PRODUCTION,
  EVENT_CATEGORY_CONTENT,
  EVENT_ACTION_COPY_LINK,
  EVENT_ACTION_DOWNLOAD,
  EVENT_ACTION_ADD,
  EVENT_ACTION_REMOVE,
  EVENT_ACTION_MOVE_TERM,
  EVENT_ACTION_REORDER,
  EVENT_TARGET_TYPE_PLANNER,
  EVENT_TARGET_TYPE_PLANNER_ITEM_GROUP,
  EVENT_ACTION_RESET,
  EVENT_ACTION_PREVIEW,
  EVENT_TARGET_TYPE_FORMAT,
  EVENT_CATEGORY_PLANNER_TERM,
  EVENT_ACTION_GO_TO,
  EVENT_DATA_COMING_SOON,
  EVENT_TARGET_TYPE_UNIT,
  EVENT_TARGET_TYPE_MODAL,
  EVENT_DATA_COMING_SOON_PREVIEW,
} from 'approot/shared/data-tracker/data-events.constants';
import { pushDataTrackerEvent } from 'approot/shared/data-tracker/data-tracker-push';
import type { UserFragment } from '../shared/user/user-data.graphql.generated';
import { TERMS } from './manage/planner.constants';
import type { Pluck } from 'lib/gql/pluck';

export function pushPlannerResetEvent(
  id: number,
  curriculums: InquisitiveCurriculum[],
  subjects: Pluck<UserFragment, 'userClasses.subjects'>[],
  years: Pluck<UserFragment, 'userClasses.years'>[]
) {
  pushDataTrackerEvent(EVENT_CATEGORY_CONTENT_PRODUCTION, EVENT_ACTION_RESET, {
    target_type: EVENT_CATEGORY_PLANNER,
    target_identifier: id,
    curriculums: curriculums,
    subjects: subjects.map(s => s.name),
    years: years.map(y => y.name),
  });
}

export function pushPlannerCopyEvent(contentType: string, contentId: number) {
  pushDataTrackerEvent(EVENT_CATEGORY_CONTENT, EVENT_ACTION_COPY_LINK, {
    target_type: contentType,
    target_identifier: contentId,
  });
}

export function pushPlannerGoToPageEvent(
  contentType: string,
  contentId: number
) {
  pushDataTrackerEvent(EVENT_CATEGORY_CONTENT, EVENT_ACTION_GO_TO, {
    target_type: contentType,
    target_identifier: contentId,
  });
}

export function pushPlannerAddEvent({
  plannerId,
  unitId,
  term,
  plannerItemGroupId,
  isComingSoon,
  isComingSoonPreview,
}: {
  plannerId: number;
  unitId: number;
  term: string;
  plannerItemGroupId: number | null;
  isComingSoon: boolean;
  isComingSoonPreview: boolean;
}) {
  pushDataTrackerEvent(EVENT_CATEGORY_PLANNER, EVENT_ACTION_ADD, {
    target_type: EVENT_TARGET_TYPE_UNIT,
    target_identifier: unitId,
    [EVENT_TARGET_TYPE_PLANNER_ITEM_GROUP]: plannerItemGroupId,
    [EVENT_CATEGORY_PLANNER]: plannerId,
    term: term,
    [EVENT_DATA_COMING_SOON]: isComingSoon,
    ...(isComingSoonPreview && {
      referrer_nav_type: EVENT_TARGET_TYPE_MODAL,
      referrer_nav_id: EVENT_DATA_COMING_SOON_PREVIEW,
    }),
  });
}

export function pushPlannerRemoveEvent(
  plannerId: number,
  unitId: number,
  term: number,
  plannerItemGroupId: number | null,
  isComingSoon: boolean,
  isComingSoonPreview: boolean
) {
  pushDataTrackerEvent(EVENT_CATEGORY_PLANNER, EVENT_ACTION_REMOVE, {
    target_type: EVENT_TARGET_TYPE_UNIT,
    target_identifier: unitId,
    [EVENT_TARGET_TYPE_PLANNER_ITEM_GROUP]: plannerItemGroupId,
    [EVENT_CATEGORY_PLANNER]: plannerId,
    term: term,
    [EVENT_DATA_COMING_SOON]: isComingSoon,
    ...(isComingSoonPreview && {
      referrer_nav_type: EVENT_TARGET_TYPE_MODAL,
      referrer_nav_id: EVENT_DATA_COMING_SOON_PREVIEW,
    }),
  });
}

export function pushPlannerMoveEvent({
  plannerId,
  unitId,
  fromTerm,
  toTerm,
  plannerItemGroupId,
  includeBelowAndAbove,
  isComingSoon,
  isComingSoonPreview,
  belowUnitId = undefined,
  aboveUnitId = undefined,
}: {
  plannerId: number;
  unitId: number;
  fromTerm: number;
  toTerm: number;
  plannerItemGroupId: number | null;
  includeBelowAndAbove: boolean;
  isComingSoon: boolean;
  isComingSoonPreview: boolean;
  belowUnitId?: number;
  aboveUnitId?: number;
}) {
  pushDataTrackerEvent(EVENT_CATEGORY_PLANNER, EVENT_ACTION_MOVE_TERM, {
    target_type: EVENT_TARGET_TYPE_UNIT,
    target_identifier: unitId,
    [EVENT_TARGET_TYPE_PLANNER_ITEM_GROUP]: plannerItemGroupId,
    [EVENT_CATEGORY_PLANNER]: plannerId,
    from: fromTerm,
    to: toTerm,
    [EVENT_DATA_COMING_SOON]: isComingSoon,
    ...(isComingSoonPreview && {
      referrer_nav_type: EVENT_TARGET_TYPE_MODAL,
      referrer_nav_id: EVENT_DATA_COMING_SOON_PREVIEW,
    }),
    ...(includeBelowAndAbove ? { above: belowUnitId || '' } : null),
    ...(includeBelowAndAbove ? { below: aboveUnitId || '' } : null),
  });
}

export function pushPlannerReorderEvent(
  unitId: number,
  plannerId: number,
  plannerItemGroupId: number | null,
  term: number,
  belowUnitId: number | undefined = undefined,
  aboveUnitId: number | undefined = undefined
) {
  pushDataTrackerEvent(EVENT_CATEGORY_PLANNER, EVENT_ACTION_REORDER, {
    target_type: EVENT_TARGET_TYPE_UNIT,
    target_identifier: unitId,
    [EVENT_TARGET_TYPE_PLANNER_ITEM_GROUP]: plannerItemGroupId,
    [EVENT_CATEGORY_PLANNER]: plannerId,
    [EVENT_CATEGORY_PLANNER_TERM]: term,
    below: aboveUnitId ? aboveUnitId : '',
    above: belowUnitId ? belowUnitId : '',
  });
}

export function pushPlannerDownloadPlannerEvent(
  plannerId: number,
  format: string,
  terms: string[],
  years: InquisitiveYears[],
  subjects: InquisitiveSubject[]
) {
  const isAllTerms = terms.length === 0 || terms.length === TERMS.length;
  pushDataTrackerEvent(EVENT_CATEGORY_PLANNER, EVENT_ACTION_DOWNLOAD, {
    target_type: EVENT_TARGET_TYPE_PLANNER,
    target_identifier: plannerId,
    [EVENT_TARGET_TYPE_FORMAT]: format,
    [EVENT_CATEGORY_PLANNER_TERM]: isAllTerms
      ? TERMS.map(t => t.value)
      : terms.map(t => parseInt(t)),
    filtered_terms: !isAllTerms,
    years: years,
    subjects: subjects,
  });
}

export function pushPlannerPreviewEvent(contentType: string, id: number) {
  pushDataTrackerEvent(EVENT_CATEGORY_CONTENT, EVENT_ACTION_PREVIEW, {
    target_type: contentType,
    target_identifier: id,
  });
}

export function pushPlannerRemoveItemEvent(
  contentType: string,
  contentId: number,
  plannerItemId: number,
  plannerId: number,
  plannerItemGroupId: number,
  unitId: number,
  term: number,
  isCompleted: boolean
) {
  pushDataTrackerEvent(EVENT_CATEGORY_PLANNER, EVENT_ACTION_REMOVE, {
    target_type: contentType,
    target_identifier: contentId,
    planner_item: plannerItemId,
    unit: unitId,
    planner_item_group: plannerItemGroupId,
    planner: plannerId,
    term: term,
    is_completed: isCompleted,
  });
}

export function pushPlannerAddItemEvent(
  contentType: string,
  contentId: number,
  plannerItemId: number,
  plannerId: number,
  plannerItemGroupId: number,
  unitId: number,
  term: number
) {
  pushDataTrackerEvent(EVENT_CATEGORY_PLANNER, EVENT_ACTION_ADD, {
    target_type: contentType,
    target_identifier: contentId,
    planner_item: plannerItemId,
    unit: unitId,
    planner_item_group: plannerItemGroupId,
    planner: plannerId,
    term: term,
  });
}
